
import React from 'react';
import { Link } from 'react-router-dom';
import PageWrapper from '@/components/utils/PageWrapper';
import { BarChart2, ArrowLeft, ArrowUpRight, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import AnimatedElement from '@/components/utils/AnimatedElement';

const DataAnalyticsPage = () => {
  return (
    <PageWrapper>
      <main className="flex-grow pt-32 pb-20">
        <div className="container mx-auto px-6">
          <div className="mb-6">
            <Link to="/services" className="text-muted-foreground hover:text-primary flex items-center transition-colors">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to all services
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="md:col-span-2">
              <AnimatedElement animation="fadeIn">
                <div className="bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400 p-3 rounded-xl inline-block mb-4">
                  <BarChart2 className="h-10 w-10" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
                  Data <span className="text-primary">Analytics</span>
                </h1>
                <p className="text-xl text-muted-foreground mb-6">
                  Transform raw data into actionable insights with our advanced analytics solutions.
                </p>
              </AnimatedElement>

              <AnimatedElement animation="slideUp" delay={0.2}>
                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <p>
                    In today's data-driven world, organizations are collecting more information than ever before. Our data analytics services help you harness this wealth of data to gain valuable insights, make informed decisions, and drive business growth.
                  </p>
                  
                  <h2>Our Approach</h2>
                  <p>
                    We combine industry expertise with cutting-edge technologies to deliver comprehensive data analytics solutions tailored to your specific needs. Our team of data scientists and analysts work closely with you to understand your business objectives and develop analytics strategies that deliver measurable results.
                  </p>
                  
                  <h2>Key Benefits</h2>
                  <ul>
                    <li>Transform complex data into clear, actionable insights</li>
                    <li>Identify trends and patterns that drive business value</li>
                    <li>Make data-driven decisions with confidence</li>
                    <li>Optimize business processes and improve operational efficiency</li>
                    <li>Gain competitive advantage through predictive analytics</li>
                    <li>Democratize data access across your organization</li>
                  </ul>
                </div>
              </AnimatedElement>
            </div>

            <div className="md:col-span-1">
              <motion.div 
                className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 shadow-subtle border border-gray-100 dark:border-gray-700 sticky top-32"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
              >
                <h3 className="text-xl font-semibold mb-4">Our Data Analytics Services</h3>
                <ul className="space-y-3">
                  {[
                    "Business Intelligence Dashboards",
                    "Predictive Analytics",
                    "Data Visualization",
                    "Real-time Analytics",
                    "ETL Processes",
                    "Custom KPI Tracking"
                  ].map((service, index) => (
                    <motion.li 
                      key={index}
                      className="flex items-start"
                      whileHover={{ x: 5 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>{service}</span>
                    </motion.li>
                  ))}
                </ul>

                <div className="mt-8">
                  <Link to="/contact">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                      <Button className="w-full btn-hover-glow group">
                        Contact Us
                        <ArrowUpRight className="ml-1 h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                      </Button>
                    </motion.div>
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>

          <motion.div 
            className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 p-8 rounded-2xl mb-16"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 300, damping: 15 }}
          >
            <h2 className="text-2xl font-semibold mb-4">Ready to leverage your data?</h2>
            <p className="text-muted-foreground mb-6">
              Let's discuss how our data analytics solutions can help you make better decisions and drive growth.
            </p>
            <Link to="/contact">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <Button className="btn-hover-glow group">
                  Get Started
                  <ArrowUpRight className="ml-1 h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Button>
              </motion.div>
            </Link>
          </motion.div>
        </div>
      </main>
    </PageWrapper>
  );
};

export default DataAnalyticsPage;
